/**
 * Mock data for Fleet Control Centre (screenshots / demos).
 * Use the "Mock" button on the overview to switch all sections to this data.
 */

import type {
  AnalyticsOverview,
  DashboardOverview,
  JigsawData,
  OptifleetData,
  QargoData,
  AquariusData,
  ApiStatus,
  Alert,
} from '../types/fleetDataHub';

const now = new Date().toISOString();
const completedStatus: ApiStatus = {
  status: 'completed',
  last_run: now,
  success_rate: 100,
  total_endpoints: 5,
  successful: 5,
  failed: 0,
  avg_response_ms: 120,
  total_records: 1000,
};

const makeVehicleId = (n: number) => `vehicle ${n}`;
const makeDriverId = (n: number) => `driver ${n}`;

const MOCK_DAILY_TRANSACTIONS: Record<string, number> = {
  '2026-01-05': 392,
  '2026-01-06': 478,
  '2026-01-07': 531,
  '2026-01-08': 445,
  '2026-01-09': 389,
  '2026-01-10': 512,
  '2026-01-11': 598,
  '2026-01-12': 423,
  '2026-01-13': 367,
  '2026-01-14': 489,
  '2026-01-15': 556,
  '2026-01-16': 612,
  '2026-01-17': 438,
  '2026-01-18': 502,
  '2026-01-19': 575,
  '2026-01-20': 421,
  '2026-01-21': 356,
  '2026-01-22': 489,
  '2026-01-23': 285,
  '2026-01-24': 412,
  '2026-01-25': 548,
  '2026-01-26': 365,
  '2026-01-27': 495,
  '2026-01-28': 621,
  '2026-01-29': 321,
  '2026-01-30': 478,
  '2026-01-31': 582,
  '2026-02-01': 445,
  '2026-02-02': 398,
  '2026-02-03': 512,
  '2026-02-04': 456,
};

/** ~£65.86 per fill-up from mock totals (187500 / 2847) */
const MOCK_AVG_SPEND_PER_TX = 187500 / 2847;

export const MOCK_JIGSAW_DATA: JigsawData = {
  vehicle_count: 142,
  driver_count: 89,
  transaction_count: 2847,
  total_fuel_litres: 125000,
  total_fuel_cost: 187500,
  tank_count: 142,
  pump_count: 28,
  daily_transactions: MOCK_DAILY_TRANSACTIONS,
  daily_spend: Object.fromEntries(
    Object.entries(MOCK_DAILY_TRANSACTIONS).map(([date, count]) => [
      date,
      Math.round(count * MOCK_AVG_SPEND_PER_TX * 100) / 100,
    ]),
  ),
  fuel_types: { Diesel: 110000, Petrol: 15000, 'Ad blue': 6500 },
  top_spending_vehicles: [
    { vehicle: makeVehicleId(1), amount: 2450 },
    { vehicle: makeVehicleId(2), amount: 2380 },
    { vehicle: makeVehicleId(3), amount: 2290 },
    { vehicle: makeVehicleId(4), amount: 2210 },
    { vehicle: makeVehicleId(5), amount: 2075 },
    { vehicle: makeVehicleId(6), amount: 2010 },
    { vehicle: makeVehicleId(7), amount: 1935 },
    { vehicle: makeVehicleId(8), amount: 1880 },
  ],
};

export const MOCK_OPTIFLEET_DATA: OptifleetData = {
  vehicle_count: 110,
  driver_count: 89,
  position_count: 45200,
  status_count: 110,
  alert_count: 2,
  avg_speed: 42,
  moving_vehicles: 3,
  idle_vehicles: 12,
  prolonged_idle_vehicles: 2,
  stopped_vehicles: 88,
  offline_vehicles: 7,
  export_timestamp: '2026-01-29T17:00:00',
  data_source: 'export',
  vehicles: [
    { id: makeVehicleId(1), status: 'Moving', speed: 22, latitude: 51.5074, longitude: -0.1278, lastUpdate: now },
    { id: makeVehicleId(2), status: 'Moving', speed: 64, latitude: 51.5150, longitude: -0.1420, lastUpdate: now },
    { id: makeVehicleId(3), status: 'Moving', speed: 86, latitude: 51.5200, longitude: -0.1500, lastUpdate: now },
    { id: makeVehicleId(4), status: 'Idle', speed: 0, latitude: 51.4500, longitude: -0.1000, lastUpdate: now, prolonged_idle: true, idle_duration_minutes: 22 },
    { id: makeVehicleId(5), status: 'Idle', speed: 0, latitude: 51.4300, longitude: -0.0900, lastUpdate: now, prolonged_idle: true, idle_duration_minutes: 29, idle_duration_truncated: true },
    { id: makeVehicleId(6), status: 'Idle', speed: 0, latitude: 51.4700, longitude: -0.0800, lastUpdate: now },
    { id: makeVehicleId(7), status: 'Idle', speed: 0, latitude: 51.4900, longitude: -0.1200, lastUpdate: now },
    { id: makeVehicleId(8), status: 'Stopped', speed: 0, latitude: 50.8500, longitude: 4.3500, lastUpdate: now },
    { id: makeVehicleId(9), status: 'Stopped', speed: 0, latitude: 51.4600, longitude: -0.1100, lastUpdate: now },
    { id: makeVehicleId(10), status: 'Stopped', speed: 0, latitude: 52.2000, longitude: 0.1200, lastUpdate: now },
    { id: makeVehicleId(11), status: 'Stopped', speed: 0, latitude: 53.4800, longitude: -2.2400, lastUpdate: now },
    { id: makeVehicleId(12), status: 'Stopped', speed: 0, latitude: 55.9533, longitude: -3.1883, lastUpdate: now },
  ],
};

export const MOCK_QARGO_DATA: QargoData = {
  order_count: 1250,
  company_count: 24,
  resource_count: 89,
  completed_orders: 1180,
  in_progress_orders: 45,
  pending_orders: 18,
  cancelled_orders: 7,
  delivery_performance: 94,
  orders_by_status: { completed: 1180, in_progress: 45, pending: 18, cancelled: 7 },
  orders_by_date: {
    '2026-01-05': 67,
    '2026-01-06': 89,
    '2026-01-07': 102,
    '2026-01-08': 78,
    '2026-01-09': 54,
    '2026-01-10': 93,
    '2026-01-11': 116,
    '2026-01-12': 71,
    '2026-01-13': 48,
    '2026-01-14': 85,
    '2026-01-15': 107,
    '2026-01-16': 122,
    '2026-01-17': 69,
    '2026-01-18': 96,
    '2026-01-19': 111,
    '2026-01-20': 65,
    '2026-01-21': 52,
    '2026-01-22': 88,
    '2026-01-23': 42,
    '2026-01-24': 78,
    '2026-01-25': 95,
    '2026-01-26': 61,
    '2026-01-27': 112,
    '2026-01-28': 124,
    '2026-01-29': 58,
    '2026-01-30': 89,
    '2026-01-31': 108,
    '2026-02-01': 73,
    '2026-02-02': 86,
    '2026-02-03': 118,
    '2026-02-04': 91,
  },
};

export const MOCK_AQUARIUS_DATA: AquariusData = {
  driver_count: 89,
  vehicle_count: 142,
  activity_count: 12500,
  infringement_count: 4,
  total_driving_hours: 12400,
  total_rest_hours: 3100,
  total_work_hours: 4200,
  total_unknown_hours: 150,
  total_driving_plus_work_hours: 16600,
  total_available_hours: 0,
  total_other_hours: 0,
  compliance_percentage: 92,
  driver_hours_compliant: 85,
  driver_details: [
    { driver_id: makeDriverId(1), driver_name: 'Driver 1', compliance_status: 'compliant', driving_hours: 48, rest_hours: 12, violations: [], rest_compliance: true, hours_compliance: true, risk_score: 5, last_activity: now },
    { driver_id: makeDriverId(2), driver_name: 'Driver 2', compliance_status: 'compliant', driving_hours: 45, rest_hours: 15, violations: [], rest_compliance: true, hours_compliance: true, risk_score: 8, last_activity: now },
    { driver_id: makeDriverId(3), driver_name: 'Driver 3', compliance_status: 'compliant', driving_hours: 39, rest_hours: 20, violations: [], rest_compliance: true, hours_compliance: true, risk_score: 4, last_activity: now },
    { driver_id: makeDriverId(4), driver_name: 'Driver 4', compliance_status: 'at-risk', driving_hours: 52, rest_hours: 8, violations: [{ type: 'Rest', description: 'Short rest', date: '2026-01-28', severity: 'minor' }], rest_compliance: false, hours_compliance: true, risk_score: 35, last_activity: now },
    { driver_id: makeDriverId(5), driver_name: 'Driver 5', compliance_status: 'compliant', driving_hours: 41, rest_hours: 16, violations: [], rest_compliance: true, hours_compliance: true, risk_score: 6, last_activity: now },
    { driver_id: makeDriverId(6), driver_name: 'Driver 6', compliance_status: 'compliant', driving_hours: 44, rest_hours: 14, violations: [], rest_compliance: true, hours_compliance: true, risk_score: 7, last_activity: now },
    { driver_id: makeDriverId(7), driver_name: 'Driver 7', compliance_status: 'at-risk', driving_hours: 56, rest_hours: 10, violations: [{ type: 'Hours', description: 'Approaching limit', date: '2026-01-27', severity: 'critical' }], rest_compliance: true, hours_compliance: false, risk_score: 28, last_activity: now },
    { driver_id: makeDriverId(8), driver_name: 'Driver 8', compliance_status: 'compliant', driving_hours: 36, rest_hours: 18, violations: [], rest_compliance: true, hours_compliance: true, risk_score: 3, last_activity: now },
    { driver_id: makeDriverId(9), driver_name: 'Driver 9', compliance_status: 'compliant', driving_hours: 49, rest_hours: 13, violations: [], rest_compliance: true, hours_compliance: true, risk_score: 9, last_activity: now },
    { driver_id: makeDriverId(10), driver_name: 'Driver 10', compliance_status: 'compliant', driving_hours: 42, rest_hours: 15, violations: [], rest_compliance: true, hours_compliance: true, risk_score: 5, last_activity: now },
  ],
};

export const MOCK_ANALYTICS_OVERVIEW: AnalyticsOverview = {
  timestamp: now,
  overall: {
    total_vehicles: 142,
    total_drivers: 89,
    total_orders: 1250,
    total_fuel_transactions: 2847,
    fleet_health_score: 88,
    compliance_score: 92,
  },
  apis: {
    jigsaw: { status: 'success', data: MOCK_JIGSAW_DATA },
    optifleet: { status: 'success', data: MOCK_OPTIFLEET_DATA },
    qargo: { status: 'success', data: MOCK_QARGO_DATA },
    aquarius: { status: 'success', data: MOCK_AQUARIUS_DATA },
  },
  alerts: [
    { severity: 'warning', message: '2 vehicles with low fuel reported in last 24 hours' },
    { severity: 'info', message: 'Driver Sam Taylor approaching weekly rest limit – review schedule' },
    { severity: 'warning', message: '3 orders delayed beyond ETA – check traffic and routing' },
    { severity: 'info', message: 'Fleet health score 88% – within target range' },
    { severity: 'critical', message: '1 vehicle VOR (off road) – maintenance required' },
  ] as Alert[],
};

export const MOCK_DASHBOARD_OVERVIEW: DashboardOverview = {
  timestamp: now,
  total_apis: 5,
  apis: {
    jigsaw: completedStatus,
    optifleet: completedStatus,
    supatrak: completedStatus,
    qargo: completedStatus,
    aquarius: completedStatus,
  },
};

/** Mock order list for Orders section (screenshot-friendly) */
export interface MockOrder {
  id: string;
  name: string;
  status?: string;
  date?: string;
  created?: { date?: string; time?: string; timestamp?: string };
  pickup?: { name?: string; address?: string; postcode?: string; date?: string };
  delivery?: { name?: string; address?: string; postcode?: string; date?: string; time?: string };
  weightKg?: number;
  volumeM3?: number;
  customerRef?: string;
  revenue?: number;
  cost?: number;
  profit?: number;
}

// Generate mock orders with dynamic dates (last ~25 days) and generic labels
const generateMockOrders = (): MockOrder[] => {
  const today = new Date();
  const orders: MockOrder[] = [];

  const statusForIndex = (i: number): string => {
    if (i % 10 === 0) return 'cancelled';
    if (i % 7 === 0) return 'pending';
    if (i % 3 === 0) return 'in_progress';
    return 'completed';
  };

  const pickupBase = (i: number) => `Pickup Point ${((i - 1) % 12) + 1}`;
  const deliveryBase = (i: number) => `Delivery Point ${((i - 1) % 12) + 1}`;
  const customerBase = (i: number) => `Customer ${((i - 1) % 18) + 1}`;

  const POSTCODE = (i: number) => `PC-${String(i).padStart(3, '0')}`;

  const ORDER_COUNT = 60;
  for (let i = 1; i <= ORDER_COUNT; i++) {
    const status = statusForIndex(i);
    const pickup = pickupBase(i);
    const delivery = deliveryBase(i);

    // Weights/volumes: realistic ranges for a demo table (kg / m3)
    const weightKg = Math.round(400 + (i % 50) * 85 + (i % 3) * 20);
    const volumeM3 = Math.round((2.0 + (i % 12) * 0.7 + (i % 5) * 0.25) * 10) / 10;

    // Revenue/cost/profit: simple deterministic relationship to keep cards consistent
    const baseRevenue = Math.round(weightKg * 1.35 + volumeM3 * 55);
    const baseCost = Math.round(baseRevenue * (0.55 + ((i % 9) / 100))); // 55% .. ~64%
    const revenue = status === 'cancelled' ? 0 : baseRevenue;
    const cost = status === 'cancelled' ? Math.round(baseCost * 0.08) : baseCost;
    const profit = revenue - cost;

    const daysAgo = Math.floor(i * 0.35); // spread across ~0..~21 days for 60 rows
    const date = new Date(today);
    date.setDate(date.getDate() - daysAgo);
    const dateStr = date.toISOString().split('T')[0];

    orders.push({
      id: `ORD-2026-${String(i).padStart(3, '0')}`,
      name: `${pickup} -> ${delivery}`,
      status,
      date: dateStr,
      created: { date: dateStr, time: '10:30', timestamp: dateStr },
      pickup: { name: pickup, address: pickup, postcode: POSTCODE(i), date: dateStr },
      delivery: { name: delivery, address: delivery, postcode: POSTCODE(i + 999), date: dateStr, time: '14:20' },
      weightKg,
      volumeM3,
      customerRef: customerBase(i),
      revenue,
      cost,
      profit,
    });
  }

  return orders;
};

export const MOCK_ORDERS: MockOrder[] = generateMockOrders();

/** Mock trip pattern data for geographic insights and vehicle statistics */
const TRIP_VEHICLE_IDS = Array.from({ length: 12 }, (_, idx) => makeVehicleId(idx + 1));

const makeLabelDistribution = (
  counts: number[],
  labels: readonly [string, string, string, string]
) => {
  const total = Math.max(1, counts.reduce((a, b) => a + b, 0));
  return labels.map((label, i) => ({
    Label: label,
    Count: counts[i] ?? 0,
    Percentage: Math.round(((counts[i] ?? 0) / total) * 100),
  }));
};

const makeSummaryStats = (i: number, kind: 'duration' | 'distance' | 'speed') => {
  // Basic deterministic variation so charts don't look copy-pasted
  const jitter = (i % 7) * 0.6;
  const base = kind === 'duration' ? 25 : kind === 'distance' ? 12 : 40;
  const mean = base + (i * 0.9) + jitter;
  const spread = kind === 'duration' ? 20 : kind === 'distance' ? 25 : 35;
  const min = Math.max(0.5, mean - spread * 0.75 + (i % 3));
  const max = mean + spread * 0.65 + (i % 4);
  const q1 = mean - spread * 0.35;
  const q2 = mean;
  const q3 = mean + spread * 0.25;
  const sampleSize = 120 + i * 2;
  return {
    SampleSize: sampleSize,
    Min: Math.round(min * 10) / 10,
    Max: Math.round(max * 10) / 10,
    Mean: Math.round(mean * 10) / 10,
    Q1: Math.round(q1 * 10) / 10,
    Q2: Math.round(q2 * 10) / 10,
    Q3: Math.round(q3 * 10) / 10,
  };
};

const makeVehicleLevel = (kind: 'duration' | 'distance' | 'speed') => {
  const vehicle_level: Record<string, any> = {};
  TRIP_VEHICLE_IDS.forEach((vehicleId, i) => {
    const counts =
      kind === 'duration'
        ? [38 + i * 2, 52 + i, 24 + (i % 5), 8 + (i % 3)]
        : kind === 'distance'
          ? [35 + i * 2, 60 + i, 22 + (i % 5), 7 + (i % 3)]
          : [30 + i * 2, 50 + i, 38 + (i % 5), 12 + (i % 3)];

    const labels =
      kind === 'duration'
        ? (['Short (0-15 min)', 'Medium (15-30 min)', 'Long (30-60 min)', 'Very Long (60+ min)'] as const)
        : kind === 'distance'
          ? (['Short (0-5 km)', 'Medium (5-15 km)', 'Long (15-30 km)', 'Very Long (30+ km)'] as const)
          : (['Very Slow (0-20 km/h)', 'Slow (20-40 km/h)', 'Moderate (40-60 km/h)', 'Fast (60+ km/h)'] as const);

    vehicle_level[vehicleId] = {
      period: 'Jan 2026',
      label_distribution: makeLabelDistribution(counts, labels),
      summary_stats: makeSummaryStats(i + 1, kind),
    };
  });
  return vehicle_level;
};

export const MOCK_TRIP_PATTERN_DATA = {
  Duration_Label: { vehicle_level: makeVehicleLevel('duration') },
  Distance_Label: { vehicle_level: makeVehicleLevel('distance') },
  Speed_Label: { vehicle_level: makeVehicleLevel('speed') },
};
